import { useEffect, useMemo, type ReactElement } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';

import { ErrorFallback } from './ErrorFallback';
import { reportError } from './reportError';

/**
 * Route-level error boundary (PTV2-1324).
 *
 * `createBrowserRouter` is a *data* router: it catches render errors thrown
 * inside the routed tree with its own boundary and renders the route's
 * `errorElement`. That interception happens *below* `RootErrorBoundary` (which
 * wraps `RouterProvider`), so without this element in-route crashes never reach
 * `reportError()` → Sentry — the SDK just shows React Router's dev error page.
 *
 * Wiring this as the top-level route's `errorElement` restores the PHI-safe
 * seam: `useRouteError()` → `reportError()` → `captureAppError()` → Sentry, and
 * the user sees our `ErrorFallback` instead of the raw stack.
 */
export function RouteErrorBoundary(): ReactElement {
  const routeError = useRouteError();

  // React Router surfaces thrown Responses (e.g. a 404 from a loader) as
  // "route error responses". We have no loaders today, so a real crash is an
  // Error — but normalize defensively so `ErrorFallback` always gets an Error
  // and we never report a non-actionable Response as an exception.
  const isResponse = isRouteErrorResponse(routeError);
  const error = useMemo<Error>(() => {
    if (routeError instanceof Error) return routeError;
    if (isRouteErrorResponse(routeError)) return new Error(`Route ${String(routeError.status)}`);
    return new Error('Unknown route error');
  }, [routeError]);

  useEffect(() => {
    // Don't report expected HTTP responses (e.g. 404) as exceptions.
    if (isResponse) return;
    reportError({ source: 'router_error_boundary', error });
  }, [error, isResponse]);

  // A data router can't reset an errored route in place, so a full reload is
  // the only reliable recovery — `canRetry={false}` drops the soft-retry button.
  return (
    <ErrorFallback
      error={error}
      canRetry={false}
      onReset={() => {
        window.location.reload();
      }}
    />
  );
}
